/* eslint-disable @next/next/no-img-element */
import { useMemo, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  limit,
  orderBy,
} from "firebase/firestore";
import { PublicProfile } from "@dimensional-engineering/dimensional-models";
import { getDownloadURL, ref } from "firebase/storage";

import styles from "./AuthSearchBar.module.css";

import { texts } from "../../../../texts.js";
import { db, firebaseStorage } from "@/_firebase/firebaseConfig.js";
import { ProfileColorMap } from "@/components/shared/ColorMap";
import GhostBox from "@/components/shared/GhostBox/GhostBox";
import SadFace from "@/components/shared/GhostBox/SadFace";
import { useDebounce } from "@/helpers/useDebounce";
import MagnifyingGlassImage from "@/components/shared/GhostBox/MagnifyingGlassImage";
import SearchIcon from "../../../publicProfiles/Header/SearchIcon";
import archetypesJsonData from "../../../../assets/traitInfo/archetypes.json";
import elementsJsonData from "../../../../assets/traitInfo/elements.json";
import patternsJsonData from "../../../../assets/traitInfo/patterns.json";
import { TraitElement } from "@/models/sharedModels";
import Element from "@/components/traits/element/Element/Element";
import Archetype from "@/components/traits/arcehtype/Archetypes/Archetype";
import Pattern from "@/components/traits/pattern/Pattern/Pattern";
import { useAuth } from "@/context/AuthContext";
import { getDefaultProfilePhoto } from "@/context/utils";
import SearchShareSummoner from "@/components/publicProfiles/Header/SearchShareSummoner";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import { useModalContext } from "@/context/ModalContext";
import FriendButton from "./FriendButton";
import ElementModal from "@/components/traits/TraitsModals/Element/ElementModal";
import ArchetypeModal from "@/components/traits/TraitsModals/Archetype/ArchetypeModal";
import PatternModal from "@/components/traits/TraitsModals/Pattern/PatternModal";
import useIsMobile from "@/helpers/useIsMobile";
import ProfileModalHandler from "@/components/authenticatedProfile/Abstracts/ProfileModal/ProfileModalHandler";

export default function AuthSearchBar() {
  const [search, setSearch] = useState<string>("");
  const [profileResults, setProfileResults] = useState<PublicProfile[] | []>(
    []
  );
  const [imagURls, setImageURls] = useState<any[] | []>([]);
  const [traitResults, setTraitResults] = useState<TraitElement[] | []>([]);
  const [searchCount, setSearchCount] = useState<number>(0);
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const { user } = useAuth();
  const connectionListWithFriendDetails = useSelector(
    (state: RootState) => state.connections.publicProfiles
  );
  const { setModalOpen, setModalComponent } = useModalContext();
  const debouncedValue = useDebounce<string>(search, 500);
  const isMobile = useIsMobile();

  useMemo(() => {
    const searchForProfiles = async () => {
      setSearchCount((current) => current + 1);
      const profileRef = collection(db, "publicProfiles");
      const firstSearch = query(
        profileRef,
        where("userNameLowerCase", "==", search.toLowerCase()),
        limit(1)
      );
      const q = query(
        profileRef,
        where("searchTerms", "array-contains", search.toLowerCase()),
        orderBy("hasPhoto", "desc"),
        limit(10)
      );
      const firstQuerySnapshot = await getDocs(firstSearch);

      const querySnapshot = await getDocs(q);
      let hitUsernameUID: string = "";
      let firstPromise = firstQuerySnapshot.docs.map((doc) => {
        hitUsernameUID = doc.data().ownerUUID;
        if (doc.data().ownerUUID !== user?.uid) {
          setProfileResults((current: any) => [...current, doc.data()]);
          return getDownloadURL(
            ref(
              firebaseStorage,
              `gs://${process.env.NEXT_PUBLIC_STORAGEBUCKET}/profileImages/${
                doc.data().ownerUUID
              }/cropped.jpg`
            )
          )
            .then((url) => {
              return url;
            })
            .catch(async (err) => {
              return await getDefaultProfilePhoto(doc.data().ownerUUID).then(
                (value) => {
                  return value;
                }
              );
            });
        }
      });
      let promises = querySnapshot.docs.map((doc) => {
        if (
          doc.data().ownerUUID !== user?.uid &&
          doc.data().ownerUUID !== hitUsernameUID
        ) {
          setProfileResults((current: any) => [...current, doc.data()]);
          return getDownloadURL(
            ref(
              firebaseStorage,
              `gs://${process.env.NEXT_PUBLIC_STORAGEBUCKET}/profileImages/${
                doc.data().ownerUUID
              }/cropped.jpg`
            )
          )
            .then((url) => {
              return url;
            })
            .catch(async (err) => {
              return await getDefaultProfilePhoto(doc.data().ownerUUID).then(
                (value) => {
                  return value;
                }
              );
            });
        }
      });
      setImageURls(await Promise.all([firstPromise, promises].flat()));
    };
    const searchForTraits = () => {
      elementsJsonData.forEach((el) => {
        if (el.isDiscoverable === true) {
          if (
            el.name.toLowerCase().includes(search.toLowerCase()) ||
            el.alias?.toLowerCase().includes(search.toLowerCase())
          ) {
            setTraitResults((current) => [
              ...current,
              el as unknown as TraitElement,
            ]);
          }
        }
      });
      archetypesJsonData.forEach((el) => {
        if (
          el.isDiscoverable === true &&
          el.archetypeGroupSlug !== "spirit" &&
          el.archetypeGroupSlug !== "values"
        ) {
          if (
            el.name.toLowerCase().includes(search.toLowerCase()) ||
            el.acronym?.toLowerCase().includes(search.toLowerCase())
          ) {
            setTraitResults((current) => [
              ...current,
              el as unknown as TraitElement,
            ]);
          }
        }
      });
      patternsJsonData.forEach((el) => {
        if (el.isDiscoverable === true) {
          if (el.name.toLowerCase().includes(search.toLowerCase())) {
            setTraitResults((current) => [
              ...current,
              el as unknown as TraitElement,
            ]);
          }
        }
      });
    };

    if (searchCount > 1000) {
      return;
    } else {
      if (search.length > 3) {
        setProfileResults([]);
        setImageURls([]);
        searchForProfiles();
      } else {
        setProfileResults([]);
        setImageURls([]);
      }
      setTraitResults([]);
      searchForTraits();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  const colorMap = ProfileColorMap;

  const mapped = profileResults.map((person, index) => {
    return (
      <div className={styles.personDiv} key={index}>
        <div
          onClick={(e) => {
            setModalComponent(
              <ProfileModalHandler
                uid={person.ownerUUID}
                elementName="profileModal"
              />
            );
          }}
          className={styles.personDiv}
        >
          <div
            className={styles.imageContainer}
            style={
              person.primaryNature?.nature
                ? {
                    border: `3px solid ${
                      colorMap[person.primaryNature?.nature]
                    }`,
                  }
                : {
                    border: `3px solid #55504F`,
                  }
            }
          >
            <img
              width={50}
              height={50}
              alt=""
              src={imagURls[index]}
              className={styles.smallImage}
            />
          </div>

          <div className={styles.bioAndUsername}>
            <h5>{person.name}</h5>
            <p>{`@${person.userName}`}</p>
          </div>
        </div>
        <FriendButton
          onSearch={() => {
            setSearchOpen(true);
          }}
          person={person}
        />
      </div>
    );
  });

  const prefilledConnectionsMapped = connectionListWithFriendDetails?.map(
    (person, index) => {
      return (
        <div className={styles.personDiv} key={index}>
          <div
            onClick={(e) => {
              setModalComponent(
                <ProfileModalHandler
                  uid={person.ownerUUID}
                  elementName="profileModal"
                />
              );
            }}
            className={styles.personDiv}
          >
            <div
              className={styles.imageContainer}
              style={
                person.primaryNature?.nature
                  ? {
                      border: `3px solid ${
                        colorMap[person.primaryNature?.nature]
                      }`,
                    }
                  : {
                      border: `3px solid #55504F`,
                    }
              }
            >
              <img
                width={50}
                height={50}
                alt=""
                src={person.imageUrl}
                className={styles.smallImage}
              />
            </div>

            <div className={styles.bioAndUsername}>
              <h5>{person.name}</h5>
              <p>{`@${person.userName}`}</p>
            </div>
          </div>
        </div>
      );
    }
  );

  const traitsMapped = traitResults.map((el, index) => {
    if (el.traitContent.traitType === "element") {
      return (
        <div
          onClick={() => {
            setModalComponent(<ElementModal slug={el.slug} />);
          }}
          style={{ cursor: "pointer" }}
          key={el.slug}
          className="elBodyFiveMArgin"
        >
          <Element slug={el.slug} />
        </div>
      );
    } else if (el.traitContent.traitType === "archetype") {
      return (
        <div
          onClick={() => {
            setModalComponent(<ArchetypeModal slug={el.slug} />);
          }}
          style={{ cursor: "pointer" }}
          key={el.slug}
          className="elBodyFiveMArgin"
        >
          <Archetype
            // @ts-ignore
            archetype={el}
          />
        </div>
      );
    } else {
      return (
        <div
          onClick={() => {
            setModalComponent(<PatternModal slug={el.slug} />);
          }}
          style={{ cursor: "pointer" }}
          key={el.slug}
          className="elBodyFiveMArgin"
        >
          <Pattern patternSlug={el.slug} />
        </div>
      );
    }
  });

  return (
    <div id="searchBarParent" className={styles.searchMain}>
      <SearchIcon dark={isMobile} />
      <input
        id="searchbarDesktop"
        placeholder={texts.searchUsersAndTraits}
        className={styles.searchBar}
        type="text"
        onChange={(e) => {
          setSearch(e.target.value);
          // handleChange(e.target.value);
        }}
        value={search}
        onFocus={() => setSearchOpen(true)}
        onBlur={() => {
          setTimeout(() => {
            setSearchOpen(false);
          }, 300);
        }}
      />
      {searchOpen && (
        <div className={styles.searchResultDivDesktop}>
          <h3>Traits</h3>
          {traitResults.length > 0 && (
            <div className={styles.traitResultDivDesktop}>{traitsMapped}</div>
          )}
          {traitResults.length === 0 && (
            <GhostBox
              onlyMessage
              message="No traits found"
              image={<SadFace />}
            />
          )}
          <h3 style={{ marginTop: "20px" }}>People</h3>
          {search.length === 0 && <div>{prefilledConnectionsMapped}</div>}
          {profileResults.length > 0 && <div>{mapped}</div>}
          {profileResults.length === 0 && search.length > 3 && (
            <GhostBox
              onlyMessage
              message="No users found"
              image={<SadFace />}
            />
          )}
          {search.length === 0 && <SearchShareSummoner />}
          {search.length < 4 && (
            <GhostBox
              image={<MagnifyingGlassImage />}
              onlyMessage={true}
              message="You must type at least 4 characters"
            />
          )}
        </div>
      )}
    </div>
  );
}
